// The web, in bands, reading downstream as you read down the page.
//
// Banded rather than force-directed, and that is the whole design. A force layout
// settles wherever the physics take it, so "upstream" and "downstream" become
// something you infer from arrow directions after squinting. Here the answer is the
// vertical position, which is the one claim this view exists to make.
//
// The bands themselves are worked out by the caller (`clientBands` / `corpusBands`
// in lib/network), because they are the question being asked and this file is only
// the answer's shape. A client's page bands what their work builds on, their work,
// and what it feeds; the Network page's Ecosystem lens bands the whole log by how
// far each record sits from a root. Same drawing, same rules, different rows.
//
// **A connection is always drawn, even when its far end cannot be named.** The
// shape of the network is not the private part; only the labels are. Dropping an
// unnameable node would shrink the web and quietly misrepresent it, which is worse
// than an unnamed circle sitting in its right place.
//
// This is the INTERNAL view, so every label is real. What the dashed ring means is
// "named here, anonymous to them" — a preview of the client's own version, not a
// redaction of this one.

import { useState } from 'react'

import { KINDS } from '../lib/model'
import { type EcoBand } from '../lib/network'
import { namedOnOthersPage, titleOf, type Connection, type State } from '../lib/reducer'

interface Placed {
  id: string
  label: string
  x: number
  y: number
  /** which band it landed in, and where along it */
  seat: number
  crowd: number
  /** drawn as a subject rather than a neighbour: filled, larger, bold */
  lead: boolean
  /** how this node reads on another client's page */
  named: 'named' | 'anonymous' | 'hidden'
}

/** The width a band is laid out across before it starts scrolling instead of shrinking. */
const BASE_W = 1000
/** First band's centre line, the gap between bands, and the room left under the last. */
const TOP = 74
const GAP = 136
const BOTTOM = 74
/** Below this a band's names fit side by side; above it every other one drops a line. */
const CROWDED = 5
/** Horizontal room one node needs before its neighbours start sitting on top of it. */
const SEAT_W = 46

export function EcosystemGraph({
  state,
  edges,
  bands,
  emphasis,
  onOpen,
}: {
  state: State
  edges: Connection[]
  /** the rows, top to bottom. An empty one still takes its place in the stack. */
  bands: EcoBand[]
  /** ids drawn as the subject of the picture — the client's own work, on their page */
  emphasis: Set<string>
  onOpen: (id: string) => void
}) {
  const [lit, setLit] = useState<string | null>(null)

  // The drawing grows downward with the bands and sideways with the widest one.
  // Shrinking a crowded band to fit instead would make the names illegible long
  // before it made them fit, so past a point the picture scrolls.
  const H = TOP + Math.max(0, bands.length - 1) * GAP + BOTTOM
  const widest = Math.max(1, ...bands.map((b) => b.ids.length))
  const W = Math.max(BASE_W, widest * SEAT_W)

  const placed = new Map<string, Placed>()
  bands.forEach((band, row) => {
    band.ids.forEach((id, i) => {
      // Evenly across the width, so a band of one sits in the middle rather than
      // hard left.
      const step = W / (band.ids.length + 1)
      const rec = state.entities[id]
      const lead = emphasis.has(id)
      placed.set(id, {
        id,
        label: titleOf(state, id) || (rec ? KINDS[rec.kind].singular : 'Unknown'),
        x: step * (i + 1),
        y: TOP + row * GAP,
        seat: i,
        crowd: band.ids.length,
        lead,
        // Their own work is named to them by definition; everything else has to
        // pass the two gates.
        named: lead ? 'named' : namedOnOthersPage(state, id),
      })
    })
  })

  const drawn = edges
    .map((e) => ({ e, a: placed.get(e.from), b: placed.get(e.to) }))
    .filter((x): x is { e: Connection; a: Placed; b: Placed } => !!x.a && !!x.b)

  if (placed.size === 0) return null

  const near = (id: string) =>
    lit === null ||
    lit === id ||
    drawn.some(({ e }) => (e.from === lit && e.to === id) || (e.to === lit && e.from === id))

  return (
    // Past the base width the picture keeps its scale and scrolls, rather than
    // shrinking every name to nothing so the widest band can fit.
    <div className="overflow-x-auto" style={{ overscrollBehaviorX: 'contain' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        style={{ height: 'auto', minWidth: W > BASE_W ? W : undefined }}
        role="img"
        aria-label={`${placed.size} connected records in ${bands.length} bands, each built on the one above it: ${bands
          .map((b) => b.label)
          .join(', ')}.`}
      >
        {bands.map((band, row) =>
          band.ids.length > 0 ? (
            <text
              key={band.key}
              x={8}
              y={TOP + row * GAP - 34}
              style={{
                fontFamily: 'var(--mono)',
                fontSize: 'var(--text-micro)',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fill: 'var(--faint)',
              }}
            >
              {band.label}
            </text>
          ) : null,
        )}

        {drawn.map(({ e, a, b }) => {
          const on = lit === null || lit === a.id || lit === b.id
          return (
            <path
              key={e.id}
              d={`M${a.x} ${a.y} C ${a.x} ${(a.y + b.y) / 2}, ${b.x} ${(a.y + b.y) / 2}, ${b.x} ${b.y}`}
              fill="none"
              stroke={e.visibility === 'internal' ? 'var(--faint)' : 'var(--marine)'}
              strokeWidth={on && lit !== null ? 1.8 : 1}
              strokeDasharray={e.visibility === 'internal' ? '3 3' : undefined}
              opacity={on ? 0.55 : 0.1}
            >
              {/* The sentence that made the edge. The prose IS the label here, so
                  hovering a line answers "why" without a second field to maintain. */}
              <title>{e.label}</title>
            </path>
          )
        })}

        {[...placed.values()].map((n) => {
          const on = near(n.id)
          const r = n.lead ? 9 : 7
          // Labels in a busy band overlap their neighbours, so every other one drops
          // a line. Truncation alone does not fix it — at a dozen nodes the names get
          // short enough to be useless before they get short enough to fit.
          const crowded = n.crowd > CROWDED
          const drop = crowded && n.seat % 2 === 1 ? 13 : 0
          // A dropped row doubles the horizontal span a name has to itself, so the
          // character budget follows the spacing rather than a fixed guess.
          const span = (W / (n.crowd + 1)) * (crowded ? 2 : 1)
          const room = Math.min(30, Math.max(12, Math.floor(span / 5.8)))
          return (
            <g
              key={n.id}
              opacity={on ? 1 : 0.18}
              onMouseEnter={() => setLit(n.id)}
              onMouseLeave={() => setLit(null)}
              onClick={() => onOpen(n.id)}
              style={{ cursor: 'pointer' }}
            >
              <circle
                cx={n.x}
                cy={n.y}
                r={r}
                fill={n.lead ? 'var(--marine)' : 'var(--panel)'}
                stroke={n.named === 'named' ? 'var(--marine)' : 'var(--faint)'}
                strokeWidth={1.6}
                // Dashed means: we can see this name, the client cannot.
                strokeDasharray={n.named === 'named' ? undefined : '3 2.5'}
              />
              {drop > 0 && (
                <line
                  x1={n.x}
                  y1={n.y + r + 2}
                  x2={n.x}
                  y2={n.y + r + drop + 2}
                  stroke="var(--faint)"
                  strokeWidth={0.6}
                />
              )}
              <text
                x={n.x}
                y={n.y + r + 14 + drop}
                textAnchor="middle"
                style={{
                  fontSize: 'var(--text-label)',
                  fill: n.lead ? 'var(--fg)' : 'var(--muted)',
                  fontWeight: n.lead ? 600 : 400,
                  fontStyle: n.named === 'named' ? undefined : 'italic',
                  paintOrder: 'stroke',
                  stroke: 'var(--panel)',
                  strokeWidth: 3,
                  strokeLinejoin: 'round',
                }}
              >
                {n.label.length > room ? `${n.label.slice(0, room - 1)}…` : n.label}
              </text>
            </g>
          )
        })}
      </svg>
    </div>
  )
}
